import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Store, Search, Phone, Globe, MapPin, User, Loader2, Check, X, MessageCircle, ChevronDown, FileDown, RefreshCw, Download, Bot, Clock } from "lucide-react";
import api, { apiError, downloadFile } from "../lib/api";
import { Input } from "./ui/input";
import { ProspectNicoleCalls } from "./NicoleCalls";

// Onglet Prospection Bruxelles pour l'espace Agent — CRM restaurants OSM.
const STATUS_META = {
  new:        { label: "Nouveau",      color: "#75807A", bg: "#F5F2EA" },
  contacted:  { label: "Contacté",     color: "#7A3A00", bg: "#FFF3E6" },
  interested: { label: "Intéressé",    color: "#166534", bg: "#DCFCE7" },
  callback:   { label: "À rappeler",   color: "#1D4ED8", bg: "#DBEAFE" },
  signed:     { label: "Signé",        color: "#065f46", bg: "#A7F3D0" },
  rejected:   { label: "Refusé",       color: "#991b1b", bg: "#FEE2E2" },
  no_answer:  { label: "Sans réponse", color: "#374151", bg: "#E5E7EB" },
};

export default function ProspectionTab({
  title = "Prospection Bruxelles",
  icon = null,
  endpointBase = "/agent/prospects",
  importPath = null,  // (facultatif) chemin d'import OSM à afficher côté admin — non utilisé ici
  extraFilters = null,  // fn (filters, setFilters) => JSX
  renderItemBadge = null,  // fn (p) => JSX pour customiser le badge (chauffeurs vs restaurants)
  contactMessage = null,  // fn (p) => string — message pré-rempli WhatsApp/SMS/Email
  emailSubject = "Partenariat Nold Go — Bruxelles",
  offerPath = null,  // (facultatif) chemin API pour envoyer l'offre PDF (ex: "/agent/prospects/{id}/send-offer")
  nicole = false,  // bouton « Appeler avec Sara Dupont » (Vapi, admin)
}) {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", commune: "", assigned: "", search: "" });
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState({}); // id → bool
  const [note, setNote] = useState({}); // id → text
  const [importing, setImporting] = useState(false);
  const [sendingOffer, setSendingOffer] = useState({}); // id → bool
  const [calling, setCalling] = useState({});
  const [settingUp, setSettingUp] = useState(false);
  const LIMIT = 25;

  const callNicole = async (p) => {
    if (!window.confirm(`Lancer un appel de Sara Dupont (IA) vers ${p.name} (${p.phone}) ?`)) return;
    setCalling((s) => ({ ...s, [p.id]: true }));
    try {
      const { data } = await api.post(`/admin/prospects/${p.id}/nicole-call`, {});
      toast.success(`Sara Dupont appelle ${p.name} (${data.number})`);
      loadItems();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCalling((s) => ({ ...s, [p.id]: false })); }
  };

  const [batching, setBatching] = useState(false);
  const batchTargets = items.filter((p) => p.status === "new" && p.phone).slice(0, 10);
  const startBatch = async () => {
    if (!batchTargets.length) return toast.error("Aucun prospect « Nouveau » avec téléphone dans cette page.");
    if (!window.confirm(`Sara Dupont va appeler ${batchTargets.length} prospect(s) l'un après l'autre :\n\n${batchTargets.map((p) => "• " + p.name).join("\n")}`)) return;
    setBatching(true);
    try {
      const { data } = await api.post("/admin/vapi/batch", { prospect_ids: batchTargets.map((p) => p.id) });
      toast.success(`${data.queued} appel(s) en file${data.business_hours ? "" : ` — démarrage au prochain créneau (${data.hours})`}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBatching(false); }
  };

  const [geo, setGeo] = useState(null);
  useEffect(() => { if (nicole) api.get("/admin/prospects/geocode").then((r) => setGeo(r.data)).catch(() => {}); }, [nicole]);
  useEffect(() => {
    if (!geo?.running) return undefined;
    const t = setInterval(() => api.get("/admin/prospects/geocode").then((r) => setGeo(r.data)), 15000);
    return () => clearInterval(t);
  }, [geo?.running]);
  const startGeo = async () => {
    try { const { data } = await api.post("/admin/prospects/geocode"); setGeo(data); toast.success(`Recherche des communes lancée (~${data.eta_min} min)`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setupNicole = async () => {
    setSettingUp(true);
    try {
      const { data } = await api.post("/admin/vapi/setup-tools", {});
      toast.success(`Sara Dupont configurée : ${data.tool_ids.length} outils attachés`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSettingUp(false); }
  };

  const sendOffer = async (p) => {
    if (!offerPath) return;
    const defaultEmail = p.email || "";
    const email = window.prompt(
      `Envoyer l'offre PDF Nold Go à ${p.name} ?\n\nSaisissez l'email du restaurateur :`,
      defaultEmail
    );
    if (!email) return;
    const lang = window.confirm("Langue de l'offre :\n\nOK = Français   /   Annuler = Néerlandais") ? "fr" : "nl";
    setSendingOffer((s) => ({ ...s, [p.id]: true }));
    try {
      const path = offerPath.replace("{id}", p.id);
      await api.post(path, { email, lang });
      toast.success(`✅ Offre PDF (${lang.toUpperCase()}) envoyée à ${email}`);
      loadItems(); loadStats();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingOffer((s) => ({ ...s, [p.id]: false })); }
  };

  const runImport = async () => {
    if (!importPath) return;
    if (!window.confirm("Lancer l'import OSM de Bruxelles ? (peut prendre 30-60s)")) return;
    setImporting(true);
    try {
      const { data } = await api.post(importPath, {});
      toast.success(`Import OK : ${data.imported || data.count || 0} fiches`);
      loadItems(); loadStats();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImporting(false); }
  };

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get(`${endpointBase}/stats`); setStats(data); } catch { /* ignore */ }
  }, [endpointBase]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`${endpointBase}?${params}`);
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [filters, offset, endpointBase]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`${endpointBase}/${id}/status`, { status, note: note[id] || undefined });
      toast.success(`Statut → ${STATUS_META[status].label}`);
      setNote((n) => ({ ...n, [id]: "" }));
      loadItems(); loadStats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const assignMe = async (id) => {
    try { await api.post(`${endpointBase}/${id}/assign`, { agent_id: "me" });
      toast.success("Attribué à vous"); loadItems(); loadStats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      await downloadFile(`${endpointBase}/export.csv?${params}`, `nold-prospects-vapi-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("CSV téléchargé (format Vapi Campaigns)");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <section data-testid="prospection-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-head font-black text-xl text-[#1A1A1A] flex items-center gap-2">
            {icon || <Store className="w-5 h-5 text-[#F26419]" />} {title}
          </h2>
          <p className="text-xs text-[#75807A]">
            {stats?.total || 0} fiches disponibles · à vous : <b>{stats?.mine?.total || 0}</b> · signés : <b className="text-[#16a34a]">{stats?.mine?.signed || 0}</b>
          </p>
        </div>
        <div className="flex gap-2">
          {importPath && (
            <button data-testid="prosp-import-osm" onClick={runImport} disabled={importing}
              className="h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#D8571A] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {importing ? "Import…" : "Importer OSM Bruxelles"}
            </button>
          )}
          {nicole && (
            <button data-testid="nicole-setup-tools" onClick={setupNicole} disabled={settingUp}
              className="h-9 px-3 rounded-lg bg-white border border-[#4F46E5] text-[#4F46E5] hover:bg-[#EEF2FF] text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors">
              {settingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />} Configurer Sara Dupont
            </button>
          )}
          {nicole && geo && (geo.remaining > 0 || geo.running) && (
            <button data-testid="prosp-geocode-btn" onClick={startGeo} disabled={geo.running}
              className="h-9 px-3 rounded-lg bg-white border border-[#059669] text-[#059669] hover:bg-[#ECFDF5] text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-80 transition-colors">
              {geo.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
              {geo.running ? `Communes : ${geo.remaining} restantes (~${geo.eta_min} min)` : `Trouver les communes (${geo.remaining})`}
            </button>
          )}
          {nicole && (
            <button data-testid="nicole-batch-btn" onClick={startBatch} disabled={batching}
              className="h-9 px-3 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors">
              {batching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />} Série Sara Dupont ({batchTargets.length})
            </button>
          )}
          <button data-testid="prosp-export-csv" onClick={exportCsv} disabled={exporting} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Exporter CSV
          </button>
          <button data-testid="prosp-refresh" onClick={() => { loadItems(); loadStats(); }} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-semibold inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Compteurs par statut */}
      {stats?.by_status && (
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <button
              key={key}
              data-testid={`prosp-filter-${key}`}
              onClick={() => { setOffset(0); setFilters((f) => ({ ...f, status: f.status === key ? "" : key })); }}
              className={`rounded-xl border-2 p-2 text-left transition-all ${filters.status === key ? "border-[#F26419] shadow-sm" : "border-[#E2DFD8] hover:border-[#F26419]/50"}`}
              style={{ background: filters.status === key ? meta.bg : "#fff" }}
            >
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</div>
              <div className="text-lg font-black" style={{ color: meta.color }}>{stats.by_status[key] || 0}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filtres recherche */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="relative sm:col-span-2">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#75807A]" />
          <Input data-testid="prosp-search" placeholder="Chercher nom, rue, téléphone…" value={filters.search}
            onChange={(e) => { setOffset(0); setFilters((f) => ({ ...f, search: e.target.value })); }}
            className="pl-9 h-10 rounded-lg border-2 text-sm" />
        </div>
        <Input data-testid="prosp-postal" placeholder="Code postal ou commune" value={filters.commune}
          onChange={(e) => { setOffset(0); setFilters((f) => ({ ...f, commune: e.target.value })); }}
          className="h-10 rounded-lg border-2 text-sm" />
        {extraFilters ? extraFilters(filters, (upd) => { setOffset(0); setFilters(upd); }) : null}
        <select data-testid="prosp-assigned" value={filters.assigned}
          onChange={(e) => { setOffset(0); setFilters((f) => ({ ...f, assigned: e.target.value })); }}
          className="h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm bg-white">
          <option value="">Tous les agents</option>
          <option value="me">Mes prospects</option>
          <option value="unassigned">Non attribués</option>
        </select>
      </div>

      {/* Liste des prospects */}
      {loading ? (
        <div className="py-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F26419]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">
          Aucun restaurant ne correspond aux filtres.
        </div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="prosp-list">
            {items.map((p) => {
              const st = STATUS_META[p.status] || STATUS_META.new;
              const isOpen = !!expanded[p.id];
              const addr = [p.housenumber, p.street].filter(Boolean).join(" ") + (p.postal_code ? ` · ${p.postal_code}` : "");
              return (
                <li key={p.id} data-testid={`prosp-item-${p.id}`} className="rounded-2xl bg-white border border-[#E2DFD8] overflow-hidden">
                  <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !isOpen }))}
                    className="w-full p-3 sm:p-4 flex items-start gap-3 text-left hover:bg-[#FDF8F0] transition-colors">
                    {renderItemBadge ? renderItemBadge(p) : (
                      <span className="shrink-0 rounded-full text-[10px] font-bold px-2 py-0.5 mt-0.5"
                        style={{ background: st.bg, color: st.color }}>{st.label}</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#1A1A1A] truncate">{p.name}</div>
                      <div className="text-xs text-[#75807A] truncate mt-0.5">
                        {addr || "—"} {p.cuisine && `· ${p.cuisine}`} {p.amenity && p.amenity !== "restaurant" && `· ${p.amenity}`}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        {p.phone && <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} className="text-[#F26419] font-semibold inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</a>}
                        {p.assigned_name && <span className="text-[#75807A] inline-flex items-center gap-1"><User className="w-3 h-3" /> {p.assigned_name}</span>}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#75807A] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="border-t border-[#F0EBE1] p-3 sm:p-4 bg-[#FDF8F0]/40 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#F26419] hover:underline"><Globe className="w-3.5 h-3.5" /> Site web</a>}
                        {p.lat && <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#F26419] hover:underline"><MapPin className="w-3.5 h-3.5" /> Voir sur Maps</a>}
                        {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1.5 text-[#F26419] hover:underline">📧 {p.email}</a>}
                        {p.opening_hours && <span className="text-[#75807A]">🕐 {p.opening_hours}</span>}
                      </div>

                      {/* Contact direct : Appel, WhatsApp, SMS, Email pré-rempli */}
                      {(p.phone || p.email) && (() => {
                        const cleanPhone = (p.phone || "").replace(/[^\d+]/g, "");
                        const waPhone = cleanPhone.startsWith("+") ? cleanPhone.slice(1) : cleanPhone;
                        const msg = contactMessage
                          ? contactMessage(p)
                          : `Bonjour ${p.name}, je suis agent Nold Go. Nous proposons la livraison de vos plats à Bruxelles sans commission excessive. Auriez-vous 2 minutes pour en discuter ? Merci !`;
                        const enc = encodeURIComponent(msg);
                        return (
                          <div className="flex flex-wrap gap-2">
                            {p.phone && (
                              <>
                                <a data-testid={`prosp-call-${p.id}`} href={`tel:${cleanPhone}`}
                                  className="h-8 px-3 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-xs font-semibold inline-flex items-center gap-1">
                                  <Phone className="w-3.5 h-3.5" /> Appeler
                                </a>
                                {nicole && (
                                  <button data-testid={`prosp-nicole-call-${p.id}`} onClick={() => callNicole(p)} disabled={calling[p.id]}
                                    className="h-8 px-3 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 transition-colors">
                                    {calling[p.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />}
                                    {calling[p.id] ? "Appel…" : "Appeler avec Sara Dupont"}
                                  </button>
                                )}
                                <a data-testid={`prosp-whatsapp-${p.id}`} href={`https://wa.me/${waPhone}?text=${enc}`} target="_blank" rel="noreferrer"
                                  className="h-8 px-3 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-semibold inline-flex items-center gap-1">
                                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                                <a data-testid={`prosp-sms-${p.id}`} href={`sms:${cleanPhone}?body=${enc}`}
                                  className="h-8 px-3 rounded-lg bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-xs font-semibold inline-flex items-center gap-1">
                                  <MessageCircle className="w-3.5 h-3.5" /> SMS
                                </a>
                              </>
                            )}
                            {p.email && (
                              <a data-testid={`prosp-email-${p.id}`}
                                href={`mailto:${p.email}?subject=${encodeURIComponent(emailSubject)}&body=${enc}`}
                                className="h-8 px-3 rounded-lg bg-[#1A1A1A] hover:bg-black text-white text-xs font-semibold inline-flex items-center gap-1">
                                📧 Email
                              </a>
                            )}
                            {offerPath && (
                              <button data-testid={`prosp-send-offer-${p.id}`} onClick={() => sendOffer(p)}
                                disabled={sendingOffer[p.id]}
                                className="h-8 px-3 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                                {sendingOffer[p.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                                {sendingOffer[p.id] ? "Envoi…" : "Envoyer offre PDF"}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      <Input value={note[p.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [p.id]: e.target.value }))}
                        placeholder="Note (optionnel) — sera enregistrée avec le changement de statut…"
                        data-testid={`prosp-note-${p.id}`}
                        className="h-9 text-sm rounded-lg border-2" />

                      <div className="flex flex-wrap gap-2">
                        <ActionBtn data-testid={`prosp-btn-contacted-${p.id}`} onClick={() => updateStatus(p.id, "contacted")}
                          bg="#FFF3E6" color="#7A3A00" icon={<Phone className="w-3.5 h-3.5" />}>Contacté</ActionBtn>
                        <ActionBtn data-testid={`prosp-btn-interested-${p.id}`} onClick={() => updateStatus(p.id, "interested")}
                          bg="#DCFCE7" color="#166534" icon={<MessageCircle className="w-3.5 h-3.5" />}>Intéressé</ActionBtn>
                        <ActionBtn data-testid={`prosp-btn-callback-${p.id}`} onClick={() => updateStatus(p.id, "callback")}
                          bg="#DBEAFE" color="#1D4ED8" icon={<Clock className="w-3.5 h-3.5" />}>À rappeler</ActionBtn>
                        <ActionBtn data-testid={`prosp-btn-signed-${p.id}`} onClick={() => updateStatus(p.id, "signed")}
                          bg="#A7F3D0" color="#065f46" icon={<Check className="w-3.5 h-3.5" />}>Signé ✍</ActionBtn>
                        <ActionBtn data-testid={`prosp-btn-no_answer-${p.id}`} onClick={() => updateStatus(p.id, "no_answer")}
                          bg="#E5E7EB" color="#374151" icon={<X className="w-3.5 h-3.5" />}>Sans réponse</ActionBtn>
                        <ActionBtn data-testid={`prosp-btn-rejected-${p.id}`} onClick={() => updateStatus(p.id, "rejected")}
                          bg="#FEE2E2" color="#991b1b" icon={<X className="w-3.5 h-3.5" />}>Refusé</ActionBtn>
                        {!p.assigned_to && (
                          <button data-testid={`prosp-btn-assign-me-${p.id}`} onClick={() => assignMe(p.id)}
                            className="h-8 px-3 rounded-lg bg-[#1A1A1A] hover:bg-black text-white text-xs font-semibold inline-flex items-center gap-1">
                            <User className="w-3.5 h-3.5" /> M'attribuer
                          </button>
                        )}
                      </div>

                      {nicole && <ProspectNicoleCalls prospectId={p.id} />}
                      {p.callback_at && p.status === "callback" && (
                        <div data-testid={`prosp-callback-at-${p.id}`} className="text-xs font-semibold text-[#1D4ED8]">Rappel prévu : {new Date(p.callback_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Brussels" })}</div>
                      )}
                      {p.notes?.length > 0 && (
                        <div className="pt-2 border-t border-[#F0EBE1] space-y-1">
                          <div className="text-[11px] font-bold uppercase tracking-wide text-[#75807A]">Historique</div>
                          {p.notes.slice(-5).reverse().map((n, i) => (
                            <div key={i} className="text-xs text-[#3B423E]">
                              <span className="text-[#75807A]">{new Date(n.at).toLocaleString("fr-BE")} · <b>{n.by_name}</b> ({STATUS_META[n.status]?.label}) :</span>{" "}
                              {n.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-[#75807A]">
              {offset + 1}–{Math.min(offset + LIMIT, total)} sur <b>{total.toLocaleString("fr-BE")}</b>
            </span>
            <div className="flex gap-2">
              <button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                data-testid="prosp-prev"
                className="h-9 px-4 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-sm font-semibold disabled:opacity-40">← Préc.</button>
              <button disabled={offset + LIMIT >= total} onClick={() => setOffset((o) => o + LIMIT)}
                data-testid="prosp-next"
                className="h-9 px-4 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-sm font-semibold disabled:opacity-40">Suiv. →</button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ActionBtn({ children, bg, color, icon, ...props }) {
  return (
    <button
      {...props}
      style={{ background: bg, color }}
      className="h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1 border border-transparent hover:border-current transition-colors"
    >
      {icon} {children}
    </button>
  );
}
